import React from 'react';
import { useLocalization } from '@assetron/common/contexts/localization/LocalizationContext';
import { Box, Typography } from '@mui/material';
import {
  TERMS_OF_SERVICES,
  PRIVACY_POLICY,
  FULLSTOP,
  PASSWORD_MIN_LENGTH,
} from '@assetron/common/constants/constant';
import AssetronButton from '@assetron/web-common/components/common/AssetronButton';
import useSignUpCreatePassword from './styles/useSignUpCreatePassword';
import AssetronHeading from '@assetron/web-common/components/common/AssetronHeading';
import { createEnterKeyHandler } from '@assetron/common/utils/keyboardHandler';
import AuthAssetronInputField from '../common/AuthAssetronInputField';

interface SignupCreatePasswordProps {
  password: string;
  setPassword: (password: string) => void;
  handleClick: () => void;
  isLoading: boolean;
  showPolicyText: boolean;
  title: string;
  buttonText: string;
}

function SignupCreatePassword({
  password,
  setPassword,
  handleClick,
  isLoading,
  showPolicyText,
  title,
  buttonText,
}: Readonly<SignupCreatePasswordProps>) {
  const { t } = useLocalization();
  const styles = useSignUpCreatePassword();

  return (
    <Box sx={styles.root}>
      <Box sx={styles.titleContainer}>
        <AssetronHeading title={title} />
        <Typography variant="body_14_regular" sx={styles.description}>
          {t('createPassword.text')}
        </Typography>
      </Box>
      <Box
        sx={styles.signupDetailsFormInputBoxStyle}
        component="form"
        onKeyDown={createEnterKeyHandler({
          isEnabled: password.length >= PASSWORD_MIN_LENGTH,
          isLoading,
          onEnter: handleClick,
        })}
      >
        <AuthAssetronInputField
          label={t('signUpDetailsPage.passwordLabel')}
          value={password}
          dataTestId="signup-password-input"
          type="password"
          onTextChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const passwordEntered = e.target.value.trim();
            setPassword(passwordEntered);
          }}
        />
      </Box>
      <Box sx={styles.button}>
        {showPolicyText && (
          <Box sx={styles.signupTextDetails}>
            <Typography
              variant="body_12_regular"
              sx={styles.description}
              component="p"
            >
              {t('signUpLandingPage.policyText1')}
              <AssetronButton
                hierarchy="link-color"
                link={TERMS_OF_SERVICES}
                newTab
              >
                {t('footer.termsOfService')}
              </AssetronButton>
              {t('signUpLandingPage.and')}
              <AssetronButton
                hierarchy="link-color"
                link={PRIVACY_POLICY}
                newTab
              >
                {t('footer.privacyPolicy')}
              </AssetronButton>
              {FULLSTOP}
            </Typography>
          </Box>
        )}
        <AssetronButton
          size="md"
          hierarchy="primary"
          disabled={password.length < PASSWORD_MIN_LENGTH}
          onClick={handleClick}
          isLoading={isLoading}
          dataTestId="signup-password-submit-button"
        >
          <Typography variant="body_14_medium">{buttonText}</Typography>
        </AssetronButton>
      </Box>
    </Box>
  );
}

export default SignupCreatePassword;
